import { useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, BookOpen, GraduationCap, Check, Clock,
  ChevronDown, ChevronUp, Mail, Users, Shield,
  Zap, BarChart3, Target, TrendingUp, Sparkles,
  DollarSign, Palette,
} from "lucide-react";

interface Lesson {
  title: string;
  duration: string;
  description: string;
  keyTakeaways: string[];
  ctaLabel?: string;
  ctaLink?: string;
}

interface Module {
  id: string;
  number: number;
  title: string;
  description: string;
  icon: any;
  color: string;
  lessons: Lesson[];
}

const MODULES: Module[] = [
  {
    id: "foundations",
    number: 1,
    title: "Email Marketing Foundations",
    description: "Everything you need to know before sending your first email. Permission, list building, and setting up for success.",
    icon: BookOpen,
    color: "bg-blue-50 text-blue-600 border-blue-200",
    lessons: [
      {
        title: "Why Email Marketing Still Wins in 2024",
        duration: "5 min",
        description: "Email delivers $42 for every $1 spent. Learn why it outperforms social media, paid ads, and SEO for direct revenue — and why it's not going away.",
        keyTakeaways: ["Email ROI vs other channels", "Owned audience vs rented audience", "Why email survives algorithm changes"],
      },
      {
        title: "Building Your First Email List (The Right Way)",
        duration: "8 min",
        description: "Avoid buying lists — it destroys deliverability. Learn the 7 proven methods for growing an organic, engaged email list from scratch.",
        keyTakeaways: ["Lead magnets that convert", "Opt-in form best practices", "Single vs double opt-in", "GDPR and CAN-SPAM basics"],
        ctaLabel: "Try Our List Growth Calculator",
        ctaLink: "/list-growth-calculator",
      },
      {
        title: "Choosing the Right Email Platform",
        duration: "6 min",
        description: "Not all email platforms are created equal. Learn what features matter most and how to evaluate your options based on your business stage.",
        keyTakeaways: ["Must-have features for every stage", "Deliverability infrastructure matters", "Automation capabilities", "Pricing models explained"],
        ctaLabel: "See MoreMail's Features",
        ctaLink: "/features",
      },
    ],
  },
  {
    id: "copywriting",
    number: 2,
    title: "Email Copywriting That Converts",
    description: "Write subject lines people open, body copy that engages, and CTAs that drive clicks. No fluff — just proven techniques.",
    icon: Palette,
    color: "bg-coral-light text-coral border-coral/20",
    lessons: [
      {
        title: "The Anatomy of a High-Converting Email",
        duration: "7 min",
        description: "Break down the structure of emails that get results: subject line, preview text, opening hook, body, and CTA. Each element has a job.",
        keyTakeaways: ["The 4-part email structure", "Opening hooks that stop the scroll", "Body copy that maintains attention", "CTA placement and design"],
        ctaLabel: "Analyze Your Email Copy",
        ctaLink: "/email-copy-analyzer",
      },
      {
        title: "Subject Lines That Get Opened",
        duration: "6 min",
        description: "Your subject line determines whether your email gets read or deleted. Learn the 8 proven formulas that consistently drive high open rates.",
        keyTakeaways: ["8 subject line formulas", "Optimal length (30-60 chars)", "Personalization boosts", "A/B testing methodology"],
        ctaLabel: "Test Your Subject Lines",
        ctaLink: "/subject-line-tester",
      },
      {
        title: "Writing CTAs That Drive Clicks",
        duration: "5 min",
        description: "The difference between a 1% and 5% click rate often comes down to your call-to-action. Learn how to write CTAs that compel action.",
        keyTakeaways: ["Action-oriented language", "Single CTA vs multiple", "Button design for email", "Creating urgency without being spammy"],
      },
    ],
  },
  {
    id: "deliverability",
    number: 3,
    title: "Deliverability Mastery",
    description: "Get your emails into the inbox — not the spam folder. SPF, DKIM, DMARC, sender reputation, and list hygiene explained.",
    icon: Shield,
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    lessons: [
      {
        title: "Email Authentication: SPF, DKIM, and DMARC",
        duration: "10 min",
        description: "These three records tell inbox providers your emails are legitimate. Without them, you're fighting an uphill battle.",
        keyTakeaways: ["What each record does", "How to set them up", "Testing your configuration", "Common mistakes to avoid"],
        ctaLabel: "Check Your Records",
        ctaLink: "/dmarc-checker",
      },
      {
        title: "Sender Reputation and How to Protect It",
        duration: "8 min",
        description: "Your sender reputation is like a credit score. Every bounce, complaint, and spam trap hit damages it. Learn how to build and maintain it.",
        keyTakeaways: ["How reputation is calculated", "Blacklist monitoring", "Complaint rate management", "IP vs domain reputation"],
        ctaLabel: "Check Your Email Health",
        ctaLink: "/email-health",
      },
      {
        title: "Domain Warmup: A Complete Guide",
        duration: "7 min",
        description: "New domains can't send 10,000 emails on day one. Learn the science behind warmup schedules and how to ramp up safely.",
        keyTakeaways: ["Why warmup is necessary", "Week-by-week volume guide", "Engagement-first strategy", "When warmup is complete"],
        ctaLabel: "Generate a Warmup Plan",
        ctaLink: "/warmup-calculator",
      },
      {
        title: "List Hygiene: Keep Your List Clean",
        duration: "6 min",
        description: "A clean list is a profitable list. Learn when to remove subscribers, how to validate emails, and the sunset policy approach.",
        keyTakeaways: ["Email validation methods", "Sunset policy explained", "Re-engagement before removal", "Bounce management"],
      },
    ],
  },
  {
    id: "segmentation",
    number: 4,
    title: "Segmentation & Personalization",
    description: "Stop sending the same email to everyone. Learn how to segment your audience and personalize at scale for dramatically better results.",
    icon: Users,
    color: "bg-purple-50 text-purple-600 border-purple-200",
    lessons: [
      {
        title: "Segmentation Strategies That Actually Work",
        duration: "8 min",
        description: "Not all segments are created equal. Learn which segmentation approaches drive the biggest lifts in engagement and revenue.",
        keyTakeaways: ["Behavioral vs demographic segments", "Engagement tier segmentation", "Purchase history segments", "Tag-based workflows"],
      },
      {
        title: "Personalization Beyond First Name",
        duration: "6 min",
        description: "Personalization goes way beyond {{first_name}}. Learn how to use dynamic content, behavioral data, and smart defaults.",
        keyTakeaways: ["Dynamic content blocks", "Behavioral personalization", "Fallback strategies", "When personalization backfires"],
      },
    ],
  },
  {
    id: "automation",
    number: 5,
    title: "Automation & Sequences",
    description: "Set up email workflows that run 24/7. Welcome series, abandoned carts, re-engagement, and more — all on autopilot.",
    icon: Zap,
    color: "bg-amber-50 text-amber-600 border-amber-200",
    lessons: [
      {
        title: "The 5 Automations Every Business Needs",
        duration: "10 min",
        description: "These five workflows run in the background and print money: welcome, abandoned cart, post-purchase, re-engagement, and birthday.",
        keyTakeaways: ["Welcome series best practices", "Cart recovery timing", "Post-purchase upsell flow", "Re-engagement triggers"],
        ctaLabel: "Install Pre-Built Automations",
        ctaLink: "/prebuilt-automations",
      },
      {
        title: "Designing Multi-Step Sequences",
        duration: "8 min",
        description: "A great sequence tells a story over time. Learn the psychology of drip campaigns and how to structure multi-email sequences.",
        keyTakeaways: ["Sequence pacing and timing", "Progressive disclosure", "Conditional branching", "When to end a sequence"],
        ctaLabel: "Browse Pre-Built Campaigns",
        ctaLink: "/prebuilt-campaigns",
      },
      {
        title: "Advanced Triggers and Conditions",
        duration: "7 min",
        description: "Go beyond simple triggers. Learn how to use behavioral events, engagement scores, and webhook data to create smart automation flows.",
        keyTakeaways: ["Engagement-based triggers", "Multi-condition workflows", "Webhook-driven automations", "A/B testing within automations"],
      },
    ],
  },
  {
    id: "analytics",
    number: 6,
    title: "Analytics & Optimization",
    description: "Measure what matters, test what works, and continuously improve. Data-driven email marketing for real results.",
    icon: BarChart3,
    color: "bg-teal-light text-teal border-teal/20",
    lessons: [
      {
        title: "Email Metrics That Actually Matter",
        duration: "6 min",
        description: "Open rate, click rate, conversion rate, revenue per email — learn which metrics to track and which to ignore.",
        keyTakeaways: ["Vanity metrics vs actionable metrics", "Benchmarking your performance", "Revenue attribution", "Cohort analysis basics"],
        ctaLabel: "Benchmark Your Metrics",
        ctaLink: "/newsletter-benchmark",
      },
      {
        title: "A/B Testing: The Definitive Guide",
        duration: "9 min",
        description: "Test everything — subject lines, send times, CTAs, content length, and more. Learn how to run tests that produce statistically meaningful results.",
        keyTakeaways: ["What to test first", "Sample size requirements", "Statistical significance", "Iterative testing framework"],
      },
      {
        title: "Calculating Your Email Marketing ROI",
        duration: "5 min",
        description: "Know exactly how much revenue your email program generates. Set up tracking, attribution models, and ROI calculations.",
        keyTakeaways: ["Revenue attribution setup", "ROI calculation formula", "Cost per subscriber", "Lifetime value from email"],
        ctaLabel: "Calculate Your ROI",
        ctaLink: "/roi-calculator",
      },
    ],
  },
];

const totalLessons = MODULES.reduce((sum, m) => sum + m.lessons.length, 0);
const totalMinutes = MODULES.reduce((sum, m) => sum + m.lessons.reduce((ls, l) => ls + parseInt(l.duration), 0), 0);

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Course",
  name: "MoreMail Email Marketing Academy",
  description: `Free email marketing course with ${totalLessons} lessons across ${MODULES.length} modules. Covers list building, copywriting, deliverability, automation, segmentation, and analytics.`,
  provider: { "@type": "Organization", name: "MoreMail", url: SITE_URL },
  isAccessibleForFree: true,
  numberOfCredits: totalLessons,
};

export default function AcademyPage() {
  const [expandedModule, setExpandedModule] = useState<string | null>("foundations");

  return (
    <>
      <SeoHead
        title={`Email Marketing Academy — ${totalLessons} Free Lessons | MoreMail`}
        description={`Free email marketing course. ${totalLessons} lessons across ${MODULES.length} modules covering list building, copywriting, deliverability, automation, and analytics. No signup required.`}
        canonical={`${SITE_URL}/academy`}
        keywords="email marketing course, email marketing tutorial, learn email marketing, email marketing guide, email copywriting course, email deliverability guide, email automation tutorial, free email marketing training"
        ogTitle={`Email Marketing Academy — ${totalLessons} Free Lessons | MoreMail`}
        ogDescription={`Master email marketing with ${totalLessons} lessons. List building, copywriting, deliverability, automation & analytics. Free, no signup needed.`}
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
            <GraduationCap size={13} className="text-coral" />
            {totalLessons} lessons • {totalMinutes} minutes • Free
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
            Email Marketing <span className="text-coral">Academy</span>
          </h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Everything you need to master email marketing — from building your first list
            to advanced automation and revenue optimization. Free, no signup required.
          </p>
          <a href="#modules"
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
            Start Learning <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-sand">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: MODULES.length.toString(), label: "Modules", icon: BookOpen },
            { value: totalLessons.toString(), label: "Lessons", icon: GraduationCap },
            { value: `${totalMinutes} min`, label: "Total Content", icon: Clock },
            { value: "Free", label: "No Signup Needed", icon: Check },
          ].map(s => (
            <div key={s.label}>
              <s.icon size={20} className="mx-auto text-coral mb-2" />
              <p className="font-display font-bold text-ink text-2xl">{s.value}</p>
              <p className="text-xs text-slate mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-3">
            Course <span className="text-coral">Curriculum</span>
          </h2>
          <p className="text-sm text-slate text-center mb-10 max-w-lg mx-auto">
            {MODULES.length} modules designed to take you from beginner to advanced. Each lesson includes
            actionable takeaways and links to tools you can use immediately.
          </p>

          <div className="space-y-4">
            {MODULES.map((mod) => {
              const isExpanded = expandedModule === mod.id;
              const Icon = mod.icon;
              return (
                <div key={mod.id} className="bg-white rounded-xl border border-sand overflow-hidden">
                  {/* Module header */}
                  <button onClick={() => setExpandedModule(isExpanded ? null : mod.id)}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-paper/50 transition-colors">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${mod.color}`}>
                      <Icon size={22} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-bold text-coral uppercase tracking-wider">Module {mod.number}</span>
                        <span className="text-[10px] text-slate">{mod.lessons.length} lessons</span>
                      </div>
                      <h3 className="font-display font-bold text-ink text-base">{mod.title}</h3>
                      <p className="text-xs text-slate mt-0.5 line-clamp-1">{mod.description}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={18} className="text-slate flex-shrink-0" /> : <ChevronDown size={18} className="text-slate flex-shrink-0" />}
                  </button>

                  {/* Lessons */}
                  {isExpanded && (
                    <div className="border-t border-sand">
                      {mod.lessons.map((lesson, li) => (
                        <div key={li} className="p-5 border-b border-sand/50 last:border-b-0">
                          <div className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-lg bg-paper flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-slate">{li + 1}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-sm font-bold text-ink">{lesson.title}</h4>
                                <span className="flex items-center gap-1 text-[10px] text-slate"><Clock size={10} /> {lesson.duration}</span>
                              </div>
                              <p className="text-xs text-slate leading-relaxed mb-3">{lesson.description}</p>

                              {/* Key takeaways */}
                              <div className="space-y-1 mb-3">
                                {lesson.keyTakeaways.map((t, ti) => (
                                  <div key={ti} className="flex items-start gap-2 text-xs text-slate">
                                    <Check size={12} className="text-teal flex-shrink-0 mt-0.5" />
                                    <span>{t}</span>
                                  </div>
                                ))}
                              </div>

                              {/* CTA link to tool */}
                              {lesson.ctaLabel && lesson.ctaLink && (
                                <Link to={lesson.ctaLink}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline">
                                  {lesson.ctaLabel} <ArrowRight size={11} />
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            Who This Is <span className="text-coral">For</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { title: "Beginners", desc: "Just getting started with email marketing? Start with Module 1 and work your way through. No prior experience needed.", icon: BookOpen },
              { title: "Growing Businesses", desc: "Already sending emails but want better results? Jump to the modules on automation, segmentation, and optimization.", icon: TrendingUp },
              { title: "Marketers & Agencies", desc: "Level up your email game with advanced deliverability, A/B testing, and revenue attribution strategies.", icon: Target },
            ].map(item => (
              <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
                <item.icon size={22} className="mx-auto text-coral mb-3" />
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Ready to Put It Into Practice?
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            Everything you learned in the Academy, MoreMail helps you execute.
            AI campaigns, pre-built automations, deliverability tools, and analytics — all in one platform.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
